import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ElementOf } from 'ts-essentials';

import type { Account } from '@libs/src/models/account.model';
import type { Group } from '@libs/src/models/group.model';
import type { PathwayMembershipStatus } from '@libs/src/models/pathway-membership.interface';
import {
  PathwayPopulated,
  PathwayUnpopulated,
} from '@libs/src/models/pathway.interface';
import type { Program } from '@libs/src/models/program.model';
import { FetchListUserPathwayMembershipsQuery } from '@libs/src/pathways/pathways-graphql.service.types.generated';
import type {
  V4GetPathwayExperiencesResponse,
  V4GetPathwaysParams,
  V4GetPathwaysResponse,
  V4GetPathwayTemplatesResponse,
} from '@libs/src/pathways/pathways.backend.types';
import type { Opportunity } from '@main-client/src/app/opportunity/opportunity.model';

type PathwayMembership = ElementOf<
  FetchListUserPathwayMembershipsQuery['pathwayMemberships']
>;

@Injectable({ providedIn: 'root' })
export class PathwaysBackend {
  constructor(private readonly http: HttpClient) {}

  v4 = {
    createPathway: (
      groupId: Group['_id'],
      pathway: Partial<PathwayPopulated>,
    ) =>
      this.http.post<{ pathway: PathwayPopulated }>(
        `/api/v4/groups/${groupId}/pathways`,
        pathway,
      ),
    createPathwayMembership: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      body: {
        account?: Account['_id'];
        email: Account['email'];
        status: PathwayMembership['status'];
      },
    ) =>
      this.http.post<{ pathwayMembership: PathwayMembership }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/memberships`,
        body,
      ),
    deletePathway: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.delete<void>(`/api/v4/groups/${groupId}/pathways/${pathwayId}`),
    deletePathwayFromOpportunity: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      opportunityId: Opportunity['_id'],
    ) =>
      this.http.delete<{
        opportunity: Opportunity;
      }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/opportunities/${opportunityId}`,
      ),
    deletePathwayFromProgram: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      programId: Program['_id'],
    ) =>
      this.http.delete<{ program: Program }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/programs/${programId}`,
      ),
    getPathway: (groupId: Group['_id'], pathwayId: PathwayPopulated['_id']) =>
      this.http.get<{ pathway: PathwayPopulated }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}`,
      ),
    getPathwayExperiences: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.get<V4GetPathwayExperiencesResponse>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/experiences`,
      ),
    getPathwayTemplates: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.get<V4GetPathwayTemplatesResponse>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/templates`,
      ),
    getPathwayUsers: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.get<Account[]>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/accounts`,
      ),
    getPathways: (groupId: Group['_id'], params?: V4GetPathwaysParams) =>
      this.http.get<V4GetPathwaysResponse>(
        `/api/v4/groups/${groupId}/pathways`,
        {
          params: {
            ...params,
          },
        },
      ),
    postPathwayAllSyncParticipants: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.post<{ pathway: PathwayUnpopulated }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/sync-all-participants`,
        {},
      ),
    postPathwayEnrollment: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      userId: Account['_id'],
      body: {
        account: Account;
      },
    ) =>
      this.http.post<{ message: string }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/send-enrollment/${userId}`,
        body,
      ),
    postPathwayJoinToken: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
    ) =>
      this.http.post<{ pathway: PathwayUnpopulated }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/join-token`,
        {},
      ),
    postPathwaysEnrollments: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      body: {
        accountIds?: Array<Account['_id']>;
        emails?: Array<Account['email']>;
        isTest: boolean;
        senderName: string;
        statuses?: PathwayMembershipStatus[];
      },
    ) =>
      this.http.post<{ message: string }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/send-enrollment-emails`,
        body,
      ),
    postPathwaysInvitations: (
      groupId: Group['_id'],
      pathwayId: PathwayPopulated['_id'],
      body: {
        accountIds: Array<Account['_id']>;
        customInformation: PathwayPopulated['invitation_email'];
        emails: Array<Account['email']>;
        senderName: string;
        senderType?: string;
        statuses?: PathwayMembershipStatus[];
      },
    ) =>
      this.http.post<{ message: string }>(
        `/api/v4/groups/${groupId}/pathways/${pathwayId}/send-invitations`,
        body,
      ),
    updatePathway: (
      groupId: Group['_id'],
      pathway: Partial<PathwayPopulated> & {
        _id: NonNullable<PathwayPopulated['_id']>;
      },
    ) =>
      this.http.patch<{ pathway: PathwayPopulated }>(
        `/api/v4/groups/${groupId}/pathways/${pathway._id}`,
        pathway,
      ),
  };
}
